'use client';

import { cn } from '@/lib/ui/cn';

interface DiscountCodeInputProps {
  discountCode: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onApply: () => void;
  isApplying: boolean;
  isEnabled: boolean;
  isApplied?: boolean;
  onRemove?: () => void;
}

export function DiscountCodeInput({
  discountCode,
  onChange,
  onApply,
  isApplying,
  isEnabled,
  isApplied = false,
  onRemove,
}: DiscountCodeInputProps) {
  const showRemove = isApplied && !!onRemove;
  const isInputDisabled = showRemove || isApplying;
  const isApplyDisabled = isApplying || !isEnabled;
  const isButtonDisabled = showRemove ? isApplying : isApplyDisabled;

  const buttonLabel = showRemove
    ? 'Remove'
    : isApplying
      ? 'Applying...'
      : 'Apply';

  const handleButtonClick = () => {
    if (showRemove) {
      onRemove?.();
    } else {
      onApply();
    }
  };

  return (
    <div className='flex items-center gap-[32px] md:gap-[60px]'>
      <input
        type='text'
        value={discountCode}
        onChange={onChange}
        disabled={isInputDisabled}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter' && !showRemove && isEnabled && !isApplying) {
            e.preventDefault();
            onApply();
          }
        }}
        placeholder='Discount Code'
        className={cn(
          'border-sh-cream text-sh-dark-gray placeholder:text-sh-dark-gray/50 h-[56px] flex-1 rounded-[40px] border-2 bg-white px-[24px] py-[16px] font-sans text-[18px] leading-[130%] font-normal transition-colors focus:border-[#292722] focus:outline-none',
          'md:h-[73px] md:px-[36px] md:py-[12px] md:text-[21px]',
          isInputDisabled && 'cursor-not-allowed opacity-60'
        )}
      />
      <button
        type='button'
        onClick={handleButtonClick}
        disabled={isButtonDisabled}
        className={cn(
          'shrink-0 text-[18px] leading-[130%] underline transition-colors md:text-[21px] md:font-bold',
          isButtonDisabled
            ? 'text-sh-gray cursor-not-allowed'
            : 'text-sh-dark-gray cursor-pointer'
        )}
      >
        {buttonLabel}
      </button>
    </div>
  );
}
